import { useHandler } from "@/hooks";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { nanoid } from "nanoid";
import type { FpUserMessageCreate } from "../../../schemas/codegen";
import { useAttachmentStore, useInputStore } from "../../state/stores";
import { api } from "../api/api";
import { projectWebSocketManager } from "../websocket";
import { useMessageManagement } from "./useMessageManagement";

export function useProjectCreation() {
  const router = useRouter();
  const { addMessage, clearProjectData } = useMessageManagement();
  const clearInput = useInputStore((state) => state.clearInput);
  const setMessage = useInputStore((state) => state.setMessage);
  const attachments = useAttachmentStore((state) => state.files);
  const clearAttachments = useAttachmentStore((state) => state.clearAll);

  const createProject = useMutation({
    mutationFn: (summary: string) =>
      api.createProject({ summary, autoGenerateName: false }),
  });

  const createNewProject = useHandler(async (message: string) => {
    const messageId = nanoid();
    addMessage({
      id: messageId,
      sender: "user",
      content: message,
      status: "completed",
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    clearInput();

    try {
      const response = await createProject.mutateAsync(message.split("\n")[0]);
      const projectId = response.data.project.id;

      const connection = projectWebSocketManager.getConnection(projectId);
      if (connection) {
        await connection.connect();

        const messageData: FpUserMessageCreate = {
          type: "user.message.create",
          messageId,
          content: message,
          attachments,
        };
        connection.send(messageData);

        // Clear attachments after sending
        clearAttachments();
      }

      await router.navigate({
        to: "/codegen/$projectId",
        params: { projectId },
        replace: true,
      });
    } catch (error) {
      clearProjectData();
      setMessage(message);
      throw error;
    }
  });

  return {
    createNewProject,
    isCreating: createProject.isPending,
  };
}
